import { useEffect, useRef, useState } from "react";

import { ChevronLeftIcon, ChevronRightIcon, EyeSlashIcon, PhotoIcon, PlayPauseIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import useLeadDocuments from "../../../data/useLeadDocuments";
import { useAuth } from "../../../contexts/AuthContext";
import { canAccessFeature } from "../../../auth/featureScopes";
import Modal from "../../../layouts/Modal";
import { documentThumbnailSrc } from "../../../models/Document";
import { ButtonInput } from "../Inputs/ButtonInput";
import classNames from './../../../utils/classNames';
import LoadingWheel from "../LoadingWheel";


//Images need to come in as any because we have IDocument and IPartImage

export default function ImageCarousel({ images, part = false, leadId, size = "w-64 h-64" }: { images: any, part?: boolean, leadId?: string, size?: string }) {
  const { t } = useTranslation("common");
  const { leadDocuments, downloadDoc } = useLeadDocuments(leadId ? leadId : "", "ImageCarousel");
  const { featureScopes } = useAuth();
  // Read here rather than threaded in as a prop: this is a shared component with several callers and
  // the answer is the same for all of them.
  const canViewProtected = canAccessFeature(featureScopes, "v5c");
  const maxScrollWidth = useRef(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState("");
  const [previewDoc, setPreviewDoc] = useState<any>(undefined);
  const [isVideo, setIsVideo] = useState(false);

  const handlePreview = (doc: any) => {
    //Protected identity document and this user hasn't got the scope - the API would refuse, so don't
    //open an empty preview modal over it
    if (doc.requiresReveal && !canViewProtected) return;

    //Part doesn't need to be downloaded
    setPreviewDoc(doc);

    if (part) {
      setPreview(doc.url);
      setPreviewOpen(true);
      return;
    }

    setPreviewOpen(true);
    setPreviewLoading(true);

    var ids = {
      leadId: leadId,
      docId: doc.id
    }

    downloadDoc.mutate(ids, {
      onSuccess: (file: any) => {
        const url = window.URL.createObjectURL(file);

        if (file.type.includes("image")) {
          setIsVideo(false);
        } else if (file.type.includes("video")) {
          setIsVideo(true);
        }

        setPreview(url);
      },
      onError: (error: any) => {
        console.error(error);
        setPreviewDoc(undefined);
      },
      onSettled: () => {
        setPreviewLoading(false);
      }
    });
  };

  const handleMovePreview = (direction: string) => {
    //get current index of the preview image
    const index = images.findIndex((doc: any) => doc.id === previewDoc.id);

    console.log(index, direction)

    //if the direction is prev and the index is greater than 0, move to the previous image
    if (direction === "prev") {
      if (index > 0) {
        handlePreview(images[index - 1]);
      }
      //if the direction is next and the index is less than the length of the images array, move to the next image
    } else {
      if (index < images.length - 1) {
        handlePreview(images[index + 1]);
      }
    }
  }

  const previewDisabled = (direction: string) => {
    if (!previewOpen) return;

    const index = images?.findIndex((doc: any) => doc?.id === previewDoc?.id);

    if (direction === "prev") {
      return index <= 0;
    }

    if (direction === "next") {
      return index >= images.length - 1;
    }

    return false;
  };

  const carousel = useRef<HTMLDivElement>(null);

  const movePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevState) => prevState - 1);
    }
  };

  const moveNext = () => {
    if (
      carousel.current !== null &&
      carousel.current.offsetWidth * currentIndex <= maxScrollWidth.current
    ) {
      setCurrentIndex((prevState) => prevState + 1);
    }
  };

  const isDisabled = (direction: string) => {
    if (direction === "prev") {
      return currentIndex <= 0;
    }

    if (direction === "next" && carousel.current !== null) {
      return (
        carousel.current.offsetWidth * currentIndex >= maxScrollWidth.current
      );
    }

    return false;
  };


  useEffect(() => {
    if (carousel !== null && carousel.current !== null) {
      carousel.current.scrollLeft = carousel.current.offsetWidth * currentIndex;
    }
  }, [currentIndex]);

  useEffect(() => {
    maxScrollWidth.current = carousel.current
      ? carousel.current.scrollWidth - carousel.current.offsetWidth
      : 0;
  }, [maxScrollWidth.current, carousel.current, carousel.current?.scrollWidth, carousel.current?.offsetWidth]);


  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (previewOpen) {
        // Check if left arrow key is pressed
        if (event.key === "ArrowLeft") {
          handleMovePreview("prev");
        }
        // Check if right arrow key is pressed
        else if (event.key === "ArrowRight") {
          handleMovePreview("next");
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [previewOpen, previewDoc]);

  const arrowCss = "hover:bg-primary-600/75 text-white w-10 h-full text-center opacity-75 hover:opacity-100 disabled:opacity-25 disabled:cursor-not-allowed z-10 p-0 m-0 transition-all ease-in-out duration-300";

  return (
    <>
      <Modal open={previewOpen} setOpen={setPreviewOpen} width="max-w-4xl">
        {previewLoading ? (
          <div className="flex justify-center items-center h-full">
            <LoadingWheel />
          </div>
        ) : (
          <>
            {/* padding bottom and right matches the padding + margins of the modal */}
            <div className="flex justify-between absolute top left w-full h-full pb-12 pr-12 ">
              <button
                onClick={() => handleMovePreview("prev")}
                className={`${arrowCss}`}
                disabled={previewDisabled("prev")}
              >
                <ChevronLeftIcon className="w-10" />
                <span className="sr-only">{t("Prev")}</span>
              </button>
              <button
                onClick={() => handleMovePreview("next")}
                className={`${arrowCss}`}
                disabled={previewDisabled("next")}
              >
                <ChevronRightIcon className="w-10 " />
                <span className="sr-only">{t("Next")}</span>
              </button>
            </div>
            <div className="flex justify-center">
              {isVideo ? (
                <video width="1024" height="576" controls>
                  <source src={preview} type="video/mp4" />
                  {t("Your browser does not support the video tag.")}
                </video>
              ) : (
                <img src={preview} alt={t("preview")} className="w-full" />
              )}
            </div>
          </>
        )}


      </Modal >

      <div className="carousel mx-auto">
        <div className="relative overflow-hidden">
          <div className="flex justify-between absolute top left w-full h-full">
            <button
              onClick={movePrev}
              className={`${arrowCss}`}
              disabled={isDisabled("prev")}
            >
              <ChevronLeftIcon className="w-10" />
              <span className="sr-only">{t("Prev")}</span>
            </button>
            <button
              onClick={moveNext}
              className={`${arrowCss}`}
              disabled={isDisabled("next")}
            >
              <ChevronRightIcon className="w-10" />
              <span className="sr-only">{t("Next")}</span>
            </button>
          </div>
          <div
            ref={carousel}
            className="carousel-container relative flex gap-2 overflow-hidden scroll-smooth snap-x snap-mandatory touch-pan-x z-0"
          >
            {images?.map((doc: any, i: number) => {
              return (
                <div
                  key={i}
                  className={classNames(size, "carousel-item text-center relative snap-start")}
                >
                  <div
                    className={`w-full h-full flex items-center justify-center border border-slate-500 rounded-md shadow-md aspect-square z-0 ${
                      doc.requiresReveal && !canViewProtected
                        ? "cursor-not-allowed"
                        : "cursor-pointer hover:bg-gray-200"
                    }`}
                    onClick={() => handlePreview(doc)}
                  >
                    {part ? (
                      //Part image is a url
                      <img src={doc.url} alt={doc.fileName!} className="object-contain w-full h-full" />
                    ) : doc.requiresReveal ? (
                      //Identity document with Documents:ProtectIdentityDocuments on. The API withheld
                      //the thumbnail from everyone; opening one needs the v5c scope, which the API
                      //also enforces - this just avoids offering a click that would 404.
                      <div className={`flex flex-col items-center justify-center gap-1 ${canViewProtected ? "text-primary-600" : "text-gray-400"}`}>
                        <EyeSlashIcon className="w-1/2 rounded-sm object-contain" />
                        <span className="text-xs font-semibold">
                          {canViewProtected ? t("Click to view") : t("No permission")}
                        </span>
                      </div>
                    ) : (
                      //Lead documents are a base64 string, or a CDN url when imported
                      documentThumbnailSrc(doc) ? (
                        <img src={documentThumbnailSrc(doc)!} alt={doc.fileName!} className="object-contain w-full h-full" />
                      )
                        :
                        //Default icons
                        (doc.contentType?.includes("video") ?
                          (<PlayPauseIcon className="w-2/3 h-2/3 rounded-full text-primary-600 border-4 border-primary-600 p-3 object-contain" />) :
                          (<PhotoIcon className="w-2/3 rounded-sm object-contain" />)
                        )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  )
}